import React, { useEffect, useState } from 'react'
import { message } from 'antd'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

import { axiosInstance } from '../../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import ProductSelectSearch from '../../../components/SelectSearch/ProductSelectSearch'
import { IOptionSelect } from '../../../components/SelectSearchMultiple'
import CircularLoading from '../../../components/CircularProgress'
import { IProduct } from '../../../types/product'
import { IProductGroupMember } from '../../../types/product-group'
import ProductGroupMembersTable from './ProductGroupMembersTable'

interface IProductGroupFormModalProps {
  isOpen: boolean
  groupId?: string
  onClose: (visible: boolean) => void
  onSaveSuccess: () => void
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('กรุณากรอกชื่อกลุ่มสินค้า'),
})

const errorMessage = (e: any) =>
  ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
  'บันทึกข้อมูลไม่สำเร็จ'

export const ProductGroupFormModal = (props: IProductGroupFormModalProps) => {
  const { isOpen, groupId, onClose, onSaveSuccess } = props
  const isUpdate = !!groupId
  const [members, setMembers] = useState<IProductGroupMember[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [pickerKey, setPickerKey] = useState<number>(0)

  const formik = useFormik<{ name: string }>({
    initialValues: { name: '' },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await axiosInstance.request({
          method: isUpdate ? 'PUT' : 'POST',
          url: isUpdate ? `/product-group/${groupId}` : '/product-group',
          data: {
            name: values.name.trim(),
            products: members.map((member) => member.objectId),
          },
        })
        message.success(
          isUpdate ? 'แก้ไขกลุ่มสินค้าสำเร็จ' : 'สร้างกลุ่มสินค้าสำเร็จ',
        )
        onClose(false)
        onSaveSuccess()
      } catch (e: any) {
        message.error(errorMessage(e))
      }
    },
  })

  useEffect(() => {
    if (!isOpen) {
      return
    }
    formik.resetForm()
    setMembers([])
    if (!groupId) {
      return
    }
    setIsLoading(true)
    axiosInstance
      .get(`/product-group/${groupId}`)
      .then(({ data }) => {
        formik.setValues({ name: data.name })
        setMembers(data.products ?? [])
      })
      .catch((e) => {
        message.error(
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
            'โหลดข้อมูลไม่สำเร็จ',
        )
        onClose(false)
      })
      .finally(() => setIsLoading(false))
  }, [isOpen, groupId])

  const addProducts = (selected: IOptionSelect<IProduct>[]) => {
    setMembers((current) => {
      const ids = new Set(current.map((member) => member.objectId))
      const added = selected
        .map((option) => option.value)
        .filter((product) => !ids.has(product.objectId))
        .map((product) => ({
          objectId: product.objectId,
          code: product.code,
          name: product.name,
        }))
      return [...current, ...added]
    })
    setPickerKey((key) => key + 1)
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isUpdate ? 'แก้ไขกลุ่มสินค้า' : 'เพิ่มกลุ่มสินค้า'}
      </DialogTitle>
      <DialogContent>
        <CircularLoading isLoading={isLoading} />
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="name"
                fullWidth
                label="กรอกชื่อกลุ่มสินค้า"
                name="name"
                helperText={
                  formik.touched.name && (formik.errors.name as string)
                }
                error={!!(formik.touched.name && formik.errors.name)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            <Grid item xs={12}>
              <ProductSelectSearch
                resetAutoComplete={String(pickerKey)}
                products={members.map((member) => member.objectId)}
                onSelect={addProducts}
              />
            </Grid>
            <Grid item xs={12}>
              <ProductGroupMembersTable
                members={members}
                onRemove={(objectId) =>
                  setMembers((current) =>
                    current.filter((member) => member.objectId !== objectId),
                  )
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
                disabled={formik.isSubmitting || isLoading}
              >
                {isUpdate ? 'บันทึก' : 'สร้างกลุ่มสินค้า'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
